import { datadogRum } from '@datadog/browser-rum';
import { link } from '@fiskil/link';
import { OpenBankingConsentUrlQuery, useOpenBankingConsentUrlQuery } from '@graphql/index';
import type { EOpenBankingError } from '@utils';

interface UseOpenBankingConsentUrlLazyProps {
  onConsentStart?: () => void;
  onConsentSuccess: () => void;
  onConsentError: (errorType?: EOpenBankingError) => void;
  applicationId: string;
}

const EXPIRY_BUFFER_MS = 30 * 1000;

const isAuthSessionExpired = (expiry?: string | number | Date | null) => {
  if (!expiry) return false;
  const expiryDate = new Date(expiry);
  if (Number.isNaN(expiryDate.getTime())) return false;
  return expiryDate.getTime() <= Date.now() + EXPIRY_BUFFER_MS;
};

export const useLazyOpenBankingConsentUrl = ({
  onConsentStart,
  onConsentSuccess,
  onConsentError,
  applicationId,
}: UseOpenBankingConsentUrlLazyProps) => {
  const { isFetching: isFetchingOpenBankingConsentDetails, refetch } = useOpenBankingConsentUrlQuery(
    { applicationId },
    { enabled: false },
  );

  const getConsentQueryData = async () => {
    const response = await refetch();
    return response?.data as OpenBankingConsentUrlQuery | undefined;
  };

  const getValidConsentData = async () => {
    const data = await getConsentQueryData();
    if (!data || isAuthSessionExpired(data?.openBankingConsentUrl?.expiry)) return undefined;
    return data;
  };

  const handleConsentFlow = async (data: OpenBankingConsentUrlQuery, hasRetried = false) => {
    const authSessionId = data?.openBankingConsentUrl?.authSessionId ?? '';
    if (!authSessionId) {
      if (!hasRetried) {
        datadogRum.addAction('AUTH_SESSION_INVALID retrying', {
          name: 'Auth Session Retry',
          values: { reason: 'missing_session_id' },
        });
        const retryConsentData = await getValidConsentData();
        if (retryConsentData) {
          await handleConsentFlow(retryConsentData, true);
          return;
        }
      }
      datadogRum.addAction('AUTH_SESSION_INVALID retry failed', {
        name: 'Auth Session Retry',
        values: { reason: 'missing_session_id' },
      });
      onConsentError();
      return;
    }

    onConsentStart?.();
    try {
      const flow = link(authSessionId);
      await flow;
      onConsentSuccess();
      if (hasRetried) {
        datadogRum.addAction('AUTH_SESSION_INVALID retry succeeded', {
          name: 'Auth Session Retry',
          values: { reason: 'invalid_session' },
        });
      }
    } catch (err) {
      if ((err as any).code === 'AUTH_SESSION_INVALID' && !hasRetried) {
        datadogRum.addAction('AUTH_SESSION_INVALID retrying', {
          name: 'Auth Session Retry',
          values: { reason: 'invalid_session' },
        });
        const retryConsentData = await getValidConsentData();
        if (retryConsentData) {
          await handleConsentFlow(retryConsentData, true);
          return;
        }
      }
      if ((err as any).code === 'AUTH_SESSION_INVALID' && hasRetried) {
        datadogRum.addAction('AUTH_SESSION_INVALID retry failed', {
          name: 'Auth Session Retry',
          values: { reason: 'invalid_session' },
        });
      }
      datadogRum.addAction('The consent request failed', {
        name: 'Failed Consent Request',
        values: {
          errorType: (err as any).code || 'unknown',
          errorDescription: (err as any).message || 'unknown',
        },
      });
      if ((err as any).code !== 'AUTH_SESSION_INVALID') {
        onConsentError((err as any).code);
      }
    }
  };

  const getOpenBankingConsentUrl = async () => {
    const data = await getValidConsentData();
    if (data) {
      await handleConsentFlow(data);
    }
  };

  return {
    isFetchingOpenBankingConsentDetails,
    getOpenBankingConsentUrl,
  };
};
